import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { faArrowRight, faArrowsRotate, faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardListWithNewProspects } from "../schema/dashboard";
import { DashboardBox } from "./DashboardBox";
import { ListProspectsRow } from "./ListProspectsRow";

interface NewDraftsBoxProps {
	lists: DashboardListWithNewProspects[];
	newDraftsTotal: number;
}

const formatTotal = (total: number) => (total >= 1000 ? "1000+" : String(total));

const sumNewProspects = (lists: DashboardListWithNewProspects[]) =>
	lists.reduce((acc, list) => acc + list.newProspectCount, 0);

export const NewDraftsBox: FunctionComponent<NewDraftsBoxProps> = ({ lists, newDraftsTotal }) => {
	const { t } = useTranslation();

	const totalNew = sumNewProspects(lists);

	return (
		<DashboardBox
			accent="indigo"
			dismissible={false}
			footer={<Link to={ROUTE_CONFIG.LISTS}>{t("dashboard.newLeads.seeAll")}</Link>}
			icon={<Icon className="w-4 h-4" icon={faArrowsRotate} />}
			subtitle={t("dashboard.newLeads.subtitle", { lists: lists.length, count: totalNew })}
			title={`${t("dashboard.newLeads.title")} (${formatTotal(totalNew)})`}
		>
			{lists.length === 0 ? (
				<div className="px-4 py-6 text-center text-xs text-text-meta">
					{t("dashboard.newLeads.empty")}
				</div>
			) : (
				<>
					{lists.map((list) => (
						<ListProspectsRow key={list.id} list={list} />
					))}
					<Link
						className="flex items-center gap-3 px-4 py-3 bg-action-200 hover:opacity-90"
						to={ROUTE_CONFIG.MESSAGE_CENTER}
					>
						<Icon className="w-4 h-4 text-brand-light shrink-0" icon={faCircleCheck} />
						<div className="flex-1 min-w-0">
							<div className="text-sm font-medium text-text truncate">
								{t("dashboard.newLeads.bulkReview")}
							</div>
							<div className="text-[11px] text-text-meta truncate">
								{t("dashboard.newLeads.bulkReviewCount", { count: newDraftsTotal })}
							</div>
						</div>
						<Icon className="w-3 h-3 text-text-meta shrink-0" icon={faArrowRight} />
					</Link>
				</>
			)}
		</DashboardBox>
	);
};
